import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthStateService } from '../services/auth-state.service';
import { ToastService } from '../services/toast.service';
import type { ApiError } from '../models/auth.model';

const SUPPRESS_TOAST_HEADER = 'X-Skip-Error-Toast';
/** Opts a request out of the global forced-logout-on-401 behavior — for best-effort background refreshes where a failure (expired token, offline) must never disrupt an otherwise-valid cached session. */
const SUPPRESS_AUTH_REDIRECT_HEADER = 'X-Skip-Auth-Redirect';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthStateService);
  const toast = inject(ToastService);

  const suppressToast = req.headers.has(SUPPRESS_TOAST_HEADER);
  const suppressAuthRedirect = req.headers.has(SUPPRESS_AUTH_REDIRECT_HEADER);
  let cleanedReq = req;
  if (suppressToast) cleanedReq = cleanedReq.clone({ headers: cleanedReq.headers.delete(SUPPRESS_TOAST_HEADER) });
  if (suppressAuthRedirect) cleanedReq = cleanedReq.clone({ headers: cleanedReq.headers.delete(SUPPRESS_AUTH_REDIRECT_HEADER) });

  return next(cleanedReq).pipe(
    catchError((err: HttpErrorResponse) => {
      const message = extractMessage(err);

      if (err.status === 401 && !suppressAuthRedirect) {
        auth.clearSession();
        void router.navigateByUrl('/login', { replaceUrl: true });
      } else if (!suppressToast) {
        void toast.error(message);
      }

      return throwError(() => new Error(message));
    }),
  );
};

function extractMessage(err: HttpErrorResponse): string {
  if (err.status === 0) {
    return 'Network error — please check your connection.';
  }
  const payload = err.error as Partial<ApiError> | string | null | undefined;
  if (typeof payload === 'string' && payload.length > 0) {
    return payload;
  }
  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string') return payload.message;
    if (typeof payload.error === 'string') return payload.error;
  }
  return err.message || 'Unexpected error occurred.';
}
